import { diaryByIdQueryOptions, syncDiaryCacheIfStale } from "@/features/diaries/api";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import { DiaryLockCall } from "@/features/diaries/components/DiaryLockCall";
import { DiaryEditDisabledProvider } from "@/features/diaries/context/DiaryEditDisabledContext";
import DiarySkeleton from "@/features/patients/components/DiarySkeleton";
import { ScrollArea, ScrollBar } from "@patient-watch/ui";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { validate } from "uuid";

const PatientDiaryPage = () => {
    const { diaryId } = Route.useParams();
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const editDisabled = Boolean(diary?.locked_at);

    return (
        <Suspense fallback={<DiarySkeleton />}>
            <DiaryEditDisabledProvider disabled={editDisabled}>
                {/* Radix wraps content in display:table, which grows to the widest child instead of fitting the screen. */}
                <ScrollArea
                    className="flex-1 min-w-0 max-w-full min-h-full"
                    viewportClassName="[&>div]:!flex [&>div]:!flex-col [&>div]:!min-w-0 [&>div]:!w-full [&>div]:!min-h-full"
                >
                    <div className="flex flex-col flex-1 p-1 pr-2 min-w-0 max-w-full min-h-full">
                        {diary?.locked_at ? <DiaryLockCall diary={diary} className="mb-3" /> : null}
                        <Outlet />
                    </div>
                    <ScrollBar />
                </ScrollArea>
            </DiaryEditDisabledProvider>
        </Suspense>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId")({
    component: PatientDiaryPage,
    loader: async ({ context, params }) => {
        if (!context?.queryClient) {
            throw new Error("Query client not available");
        }

        const diaryId = params.diaryId;
        if (!diaryId || !validate(diaryId)) {
            return { diary: null };
        }

        // Fetch the diary by ID - this returns computed/fresh tab and diary_status
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(diaryId));

        // If diary was fetched, check if cached list data is stale and invalidate if needed
        if (diary) {
            await syncDiaryCacheIfStale(context.queryClient, diary);
        }

        return { diary };
    },
});
